import memoize from 'fast-memoize';
import invariant from 'invariant';
import get from 'lodash/get';
import noop from 'lodash/noop';

import config from 'Config';
import { BUILD_NUMBER } from 'Lib/appVersion';
import { getContextByName } from 'Lib/globalContext';
import getLocaleFromLang from 'Lib/localeHelper';
import { errorHandlers, EXECUTOR_SERVICE, type ExecutorType } from 'Lib/sagaHelpers';
import { localStorage as storage } from 'Lib/storage';
import getDeviceId from 'Lib/utils/deviceId';
import fetch from 'Lib/utils/fetch';
import { milliseconds as perfTimer } from 'Lib/utils/perfTimer';
import ApiClient from 'Services/lib/ApiClient';

import { getCurrentLanguage } from 'Store/internationalization/selectors';
import {
  getAppSessionId,
  getAppSessionTimeElapsed,
  getUserSessionId,
  getUserSessionTimeElapsed,
} from 'Store/session/selectors';

import {
  ACCOUNT_SERVICE,
  APPEALS_SERVICE,
  AUTH_SERVICE,
  AV_SERVICE,
  CMP_SERVICE,
  CONTACTS_SERVICE,
  DYNAMIC_UI_SERVICE,
  EXPERIMENTS_SERVICE,
  EXPLORE_SERVICE,
  FEEDBACK_SERVICE,
  GIF_SERVICE,
  INSENDIO_SERVICE,
  MEDIA_SERVICE,
  META_SERVICE,
  MM_SERVICE,
  OFFER_SERVICE,
  ONBOARDING_SERVICE,
  PASSPORT_SERVICE,
  PAYWALL_SERVICE,
  PROFILE_SERVICE,
  PURCHASE_SERVICE,
  RECS_SERVICE,
  REPORT_SERVICE,
  SPOTIFY_SERVICE,
  STACKS_UTIL_SERVICE,
  THEMES_SERVICE,
  TINDER_SELECT_SERVICE,
  TINDER_U_SERVICE,
  USER_CONNECTIONS_SERVICE,
  USER_SERVICE,
  VAULT_SERVICE,
  VENDOR_USER_TOKENS_SERVICE,
  VERIFICATION_SERVICE,
} from './constants';
// Critical service
import ExperimentsService from './ExperimentsService';
import Service, { ServiceOptions } from './Service';

// Essential service
const AccountService = () => import('./AccountService' /* webpackChunkName: "pc-services" */);
const AuthService = () => import('./AuthService' /* webpackChunkName: "pc-services" */);
const CMPService = () => import('./CMPService' /* webpackChunkName: "pc-services" */);
const DynamicUIService = () => import('./DynamicUIService' /* webpackChunkName: "pc-services" */);
const MediaService = () => import('./MediaService' /* webpackChunkName: "pc-services" */);
const MetaService = () => import('./MetaService' /* webpackChunkName: "pc-services" */);
const MMService = () => import('./MMService' /* webpackChunkName: "pc-services" */);
const OnboardingService = () => import('./OnboardingService' /* webpackChunkName: "pc-services" */);
const OfferService = () => import('./OfferService' /* webpackChunkName: "pc-services" */);
const PaywallService = () => import('./PaywallService' /* webpackChunkName: "pc-services" */);
const ProfileService = () => import('./ProfileService' /* webpackChunkName: "pc-services" */);
const PurchaseService = () => import('./PurchaseService' /* webpackChunkName: "pc-services" */);
const RecsService = () => import('./RecsService' /* webpackChunkName: "pc-services" */);
const UserService = () => import('./UserService' /* webpackChunkName: "pc-services" */);
// Secondary service
const AppealsService = () => import('./AppealsService' /* webpackChunkName: "pc-services-2" */);
const AVService = () => import('./AVService' /* webpackChunkName: "pc-services-2" */);
const ContactsService = () => import('./ContactsService' /* webpackChunkName: "pc-services-2" */);
const ExploreService = () => import('./ExploreService' /* webpackChunkName: "pc-services-2" */);
const FeedbackService = () => import('./FeedbackService' /* webpackChunkName: "pc-services-2" */);
const GifService = () => import('./GifService' /* webpackChunkName: "pc-services-2" */);
const InsendioService = () => import('./InsendioService' /* webpackChunkName: "pc-services-2" */);
const PassportService = () => import('./PassportService' /* webpackChunkName: "pc-services-2" */);
const ReportService = () => import('./ReportService' /* webpackChunkName: "pc-services" */);
const SpotifyService = () => import('./SpotifyService' /* webpackChunkName: "pc-services-2" */);
const StacksUtilService = () =>
  import('./StacksUtilService' /* webpackChunkName: "pc-dev-services" */);
const TinderSelectService = () =>
  import('./TinderSelectService' /* webpackChunkName: "pc-services-2" */);
const TinderUService = () => import('./TinderUService' /* webpackChunkName: "pc-services-2" */);
const ThemesService = () => import('./ThemesService' /* webpackChunkName: "pc-services-2" */);
const UserConnectionsService = () =>
  import('./UserConnectionsService' /* webpackChunkName: "pc-services-2" */);
const VaultService = () => import('./VaultService' /* webpackChunkName: "pc-services-2" */);
const VendorUserTokensService = () =>
  import('./VendorUserTokensService' /* webpackChunkName: "pc-services-2" */);
const VerificationService = () => import('./VerificationsService' /* pc-services-2 */);

const SERVICE_MAP = {
  [ACCOUNT_SERVICE]: AccountService,
  [APPEALS_SERVICE]: AppealsService,
  [AUTH_SERVICE]: AuthService,
  [AV_SERVICE]: AVService,
  [CMP_SERVICE]: CMPService,
  [CONTACTS_SERVICE]: ContactsService,
  [DYNAMIC_UI_SERVICE]: DynamicUIService,
  [EXPERIMENTS_SERVICE]: ExperimentsService,
  [EXPLORE_SERVICE]: ExploreService,
  [FEEDBACK_SERVICE]: FeedbackService,
  [GIF_SERVICE]: GifService,
  [INSENDIO_SERVICE]: InsendioService,
  [MEDIA_SERVICE]: MediaService,
  [META_SERVICE]: MetaService,
  [MM_SERVICE]: MMService,
  [OFFER_SERVICE]: OfferService,
  [ONBOARDING_SERVICE]: OnboardingService,
  [PASSPORT_SERVICE]: PassportService,
  [PAYWALL_SERVICE]: PaywallService,
  [PROFILE_SERVICE]: ProfileService,
  [PURCHASE_SERVICE]: PurchaseService,
  [RECS_SERVICE]: RecsService,
  [REPORT_SERVICE]: ReportService,
  [SPOTIFY_SERVICE]: SpotifyService,
  [STACKS_UTIL_SERVICE]: StacksUtilService,
  [THEMES_SERVICE]: ThemesService,
  [TINDER_SELECT_SERVICE]: TinderSelectService,
  [TINDER_U_SERVICE]: TinderUService,
  [USER_CONNECTIONS_SERVICE]: UserConnectionsService,
  [USER_SERVICE]: UserService,
  [VAULT_SERVICE]: VaultService,
  [VENDOR_USER_TOKENS_SERVICE]: VendorUserTokensService,
  [VERIFICATION_SERVICE]: VerificationService,
};

export const KEY_API_TOKEN = 'TinderWeb/APIToken';

const deviceId = getDeviceId();

type ServiceMap = typeof SERVICE_MAP;
export type ServiceType = keyof ServiceMap;

type GenericClass = abstract new (...args: any) => any;
type VerifyIsGenericClass<T> = T extends GenericClass ? T : never;

type GetServiceByName<T extends ServiceType> =
  // Though it should be guaranteed that any extracted types extend GenericClass,
  // adding a guard to satisfy type logic in createServiceFn
  VerifyIsGenericClass<
    // If the value is a "delegated service", extract the service type from the import Promise
    ServiceMap[T] extends () => Promise<{
      default: infer TDelegatedService;
    }>
      ? TDelegatedService
      : ServiceMap[T]
  >;

type GenericFunction = (...args: any) => any;
type GetServiceClassMethods<
  TClass extends abstract new (...args: any[]) => Service,
  TInstance = InstanceType<TClass>
> = {
  [K in keyof TInstance]: TInstance[K] extends GenericFunction ? K : never;
}[keyof TInstance];

interface IService<T extends Service> {
  new (options: ServiceOptions): T;
}

/**
 * Class to encapsulate the registering, initialization, and retrieval of services
 */
class Services {
  acceptLanguage?: string;
  apiClient: ApiClient | undefined;
  deferredServices: Set<string> = new Set();
  dispatch?: Function;
  getState?: Function;
  initialized: boolean = false;
  registeredServices: any = {};

  handleTokenReceived(token: string) {
    storage.setItem(KEY_API_TOKEN, token);
  }

  /**
   * Registers a service with the class so it can be init'd later on
   * @param  {string} name    [description]
   * @param  {service} service [description]
   */
  register(name: ServiceType, service: any) {
    if (!this.registeredServices[name]) {
      const classConfig = config.services[name];
      this.registeredServices[name] = {
        config: classConfig,
        class: service,
        instance: null,
      };
    }
  }

  /**
   * Returns the locale of the app
   */
  getLocale() {
    const state = this.getState?.();
    const locale = state && getLocaleFromLang(getCurrentLanguage(state));
    return locale;
  }

  getAcceptLanguage() {
    const locale = this.getLocale();
    if (this.acceptLanguage?.startsWith(locale)) {
      return this.acceptLanguage;
    }
    const acceptLanguage = Array.from(
      new Set([locale].concat(__CLIENT__ ? navigator?.languages : []).filter(Boolean))
    ).join(',');
    this.acceptLanguage = acceptLanguage;
    return acceptLanguage;
  }

  getSessionData() {
    const state = this.getState?.();
    const now = perfTimer();
    return !state
      ? {}
      : {
          appSessionId: getAppSessionId(state),
          appSessionTimeElapsed: getAppSessionTimeElapsed(state, now),
          userSessionId: getUserSessionId(state),
          userSessionTimeElapsed: getUserSessionTimeElapsed(state, now),
        };
  }

  getTestId() {
    return storage.getItem('TEST_ID');
  }

  /**
   * Inits all the registered services with an instance of the apiClient. This should only
   * be called once per app session and will error out if you try to call it multiple times.
   * @param  {Function} getState a way to obtain the state of the app
   */
  init(getState: Function = noop, dispatch: Function = noop, onServicesInit?: Function) {
    invariant(!this.initialized, 'Services has already initialized');

    this.getState = getState;
    this.dispatch = dispatch;

    const apiToken = storage.getItem(KEY_API_TOKEN) || getState()?.context?.remoteApiToken;
    const apiClient = new ApiClient({
      acceptLanguage: this.getAcceptLanguage(),
      apiBase: process.env.CLIENT_API || '',
      apiToken,
      build: BUILD_NUMBER,
      deviceId,
      fetch,
      getAcceptLanguage: this.getAcceptLanguage.bind(this),
      getLocale: this.getLocale.bind(this),
      getSessionData: this.getSessionData.bind(this),
      getTestId: __TEST__ ? this.getTestId : null,
      locale: this.getLocale(),
      onTokenReceived: this.handleTokenReceived.bind(this),
      platformHeader: config.platformHeader,
      useWebp: !!getContextByName('webp'),
      version: process.env.VERSION,
    });

    this.apiClient = apiClient;

    const registeredServicesName = Object.entries(this.registeredServices);

    registeredServicesName.forEach(([name, value]) => {
      const RegisteredService = get(value, ['class']);
      if (RegisteredService.prototype instanceof Service) {
        this.createServiceInstance(name, RegisteredService);
      } else {
        // deferred
        this.deferredServices.add(name);
      }
    });

    this.initialized = true;

    if (onServicesInit) {
      onServicesInit();
    }
  }

  createServiceInstance = <T extends Service>(name: string, RegisteredService: IService<T>) => {
    invariant(this.apiClient, 'apiClient is not defined');
    this.registeredServices[name].instance = new RegisteredService({
      apiClient: this.apiClient,
      config: this.registeredServices[name].config,
    });
  };

  /**
   * Returns the instance of the registered service
   * @param  {[type]} name The name used to register the service
   * @return {Service}      The instance of the registered service
   */
  async getService(name: ServiceType) {
    invariant(this.initialized, 'Services have not been initialized');

    const service = get(this.registeredServices, [name, 'instance']);
    if (!service && this.deferredServices.has(name)) {
      // lazy load
      await this.deferRegisterService(name);
    } else {
      invariant(service, `Service ${name} does not exist!`);
    }

    return get(this.registeredServices, [name, 'instance']);
  }

  async deferRegisterService(name: string) {
    invariant(name, 'Service name is required to be registered');
    const getServiceClass = get(this.registeredServices, [name, 'class']);
    const { default: RegisteredService } = await getServiceClass();
    this.createServiceInstance(name, RegisteredService);
  }
}

const services = new Services();

services.register(ACCOUNT_SERVICE, AccountService);
services.register(APPEALS_SERVICE, AppealsService);
services.register(AUTH_SERVICE, AuthService);
services.register(AV_SERVICE, AVService);
services.register(CMP_SERVICE, CMPService);
services.register(CONTACTS_SERVICE, ContactsService);
services.register(DYNAMIC_UI_SERVICE, DynamicUIService);
services.register(EXPERIMENTS_SERVICE, ExperimentsService);
services.register(EXPLORE_SERVICE, ExploreService);
services.register(FEEDBACK_SERVICE, FeedbackService);
services.register(GIF_SERVICE, GifService);
services.register(INSENDIO_SERVICE, InsendioService);
services.register(MEDIA_SERVICE, MediaService);
services.register(META_SERVICE, MetaService);
services.register(MM_SERVICE, MMService);
services.register(OFFER_SERVICE, OfferService);
services.register(ONBOARDING_SERVICE, OnboardingService);
services.register(PASSPORT_SERVICE, PassportService);
services.register(PAYWALL_SERVICE, PaywallService);
services.register(PROFILE_SERVICE, ProfileService);
services.register(PURCHASE_SERVICE, PurchaseService);
services.register(RECS_SERVICE, RecsService);
services.register(REPORT_SERVICE, ReportService);
services.register(SPOTIFY_SERVICE, SpotifyService);
services.register(STACKS_UTIL_SERVICE, StacksUtilService);
services.register(THEMES_SERVICE, ThemesService);
services.register(TINDER_U_SERVICE, TinderUService);
services.register(TINDER_SELECT_SERVICE, TinderSelectService);
services.register(USER_SERVICE, UserService);
services.register(USER_CONNECTIONS_SERVICE, UserConnectionsService);
services.register(VAULT_SERVICE, VaultService);
services.register(VENDOR_USER_TOKENS_SERVICE, VendorUserTokensService);
services.register(VERIFICATION_SERVICE, VerificationService);

const createServiceFn = async <
  // None of these generics need to be explicitly passed, though
  // defining them this way makes it simpler to compose them
  TServiceName extends ServiceType,
  TServiceClass extends GetServiceByName<TServiceName>,
  TServiceInstance extends InstanceType<TServiceClass>,
  TServiceFnName extends GetServiceClassMethods<TServiceClass>,
  TServiceFn extends TServiceInstance[TServiceFnName]
>(
  service: TServiceName,
  fn: TServiceFnName,
  executor: ExecutorType = EXECUTOR_SERVICE
): Promise<TServiceFn> => {
  const serviceInstance = await services.getService(service);
  invariant(serviceInstance, `Service instance: ${service} does not exist!`);

  const serviceInstanceFn = serviceInstance[fn];
  const errorHandler = errorHandlers[executor];

  invariant(serviceInstanceFn, `Service method: ${String(fn)} does not exist!`);
  invariant(errorHandler, `Service errorHandler: ${executor} does not exist!`);

  return ((...args) => errorHandler([serviceInstance, fn], ...args)) as unknown as TServiceFn;
};

/**
 * Returns the instance of the registered service wrapped with an errorHandler.
 * If no executor is supplied, then it will use the default
 * @param  {string} name The name used to register the service
 * @param  {string} fn The method on the service
 * @param  {string} executor The error handler enumerated type (i.e. EXECUTOR_SERVICE)
 * @return {Function} The instance of the registered service wrapped with an errorHandler
 */
export const getServiceFn = memoize(createServiceFn);

export default services;
